import { useState, useEffect } from "react";
import { floorPlanAPI } from "../utils/api";

export default function FloorPlanPage({ onAskAI }) {
  const [width, setWidth] = useState(30);
  const [length, setLength] = useState(50);
  const [facing, setFacing] = useState("North");
  const [floors, setFloors] = useState(1);

  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGeneratePlan = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await floorPlanAPI.generate({
        width_ft: parseFloat(width),
        length_ft: parseFloat(length),
        facing,
        floors: parseInt(floors)
      });
      if (res.success) {
        setPlanData(res.data);
      }
    } catch (err) {
      setError(err.message || "Failed to generate floor plan layout");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGeneratePlan();
  }, []);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">📐 2D Floor Plan Visualizer & Setback Engine</h1>
          <p className="page-subtitle">
            Generate 2D architectural room layouts with NBC municipal setback rules and Vastu direction zoning.
          </p>
        </div>
        <div className="badge-pill">NBC 2016 Setbacks • Vastu Aligned</div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Controls Form Card */}
      <div className="card form-card" style={{ marginBottom: "24px" }}>
        <h3 className="card-title">Plot Dimensions & Orientation</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Plot Width (feet)</label>
            <input
              type="number"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              className="input-field"
              min="15"
              max="200"
            />
          </div>

          <div className="form-group">
            <label>Plot Length / Depth (feet)</label>
            <input
              type="number"
              value={length}
              onChange={(e) => setLength(e.target.value)}
              className="input-field"
              min="20"
              max="200"
            />
          </div>

          <div className="form-group">
            <label>Main Entrance Facing</label>
            <select value={facing} onChange={(e) => setFacing(e.target.value)} className="select-input">
              <option value="North">North Facing (Ideal)</option>
              <option value="East">East Facing (Auspicious)</option>
              <option value="West">West Facing (Neutral)</option>
              <option value="South">South Facing (Requires Remedy)</option>
            </select>
          </div>

          <div className="form-group">
            <label>Floors Structure</label>
            <select value={floors} onChange={(e) => setFloors(e.target.value)} className="select-input">
              <option value="1">Ground Floor Only (G+0)</option>
              <option value="2">G + 1 Floor (Duplex)</option>
              <option value="3">G + 2 Floors</option>
            </select>
          </div>
        </div>

        <button onClick={handleGeneratePlan} disabled={loading} className="btn btn-primary btn-full">
          {loading ? "Calculating Layout..." : "📐 Generate 2D Layout & NBC Setbacks"}
        </button>
      </div>

      {/* Results Section */}
      {planData && (
        <div>
          {/* Metrics Overview */}
          <div className="stats-grid" style={{ marginBottom: "24px" }}>
            <div className="stat-box">
              <div className="stat-label">Plot Area</div>
              <div className="stat-value">{planData.plot.plot_area_sqft} sq ft</div>
              <div className="stat-desc">{width}' × {length}' Plot</div>
            </div>

            <div className="stat-box">
              <div className="stat-label">Built-up per Floor</div>
              <div className="stat-value" style={{ color: "var(--saffron)" }}>
                {planData.setbacks.building_width_ft}' × {planData.setbacks.building_length_ft}'
              </div>
              <div className="stat-desc">Carpet: ~{planData.plot.carpet_area_sqft} sq ft</div>
            </div>

            <div className="stat-box">
              <div className="stat-label">Front Setback (NBC)</div>
              <div className="stat-value">{planData.setbacks.front_ft} feet</div>
              <div className="stat-desc">Rear: {planData.setbacks.rear_ft}ft • Sides: {planData.setbacks.side_left_ft}ft</div>
            </div>

            <div className="stat-box">
              <div className="stat-label">Overall Vastu Rating</div>
              <div className="stat-value" style={{ color: "var(--green)" }}>{planData.vastu_summary.overall_vastu_score} / 100</div>
              <div className="stat-desc">{planData.vastu_summary.entrance_direction}</div>
            </div>
          </div>

          {/* SVG Visual Blueprint Canvas */}
          <div className="card" style={{ padding: "24px", marginBottom: "24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h3 style={{ margin: 0, color: "var(--charcoal)" }}>📐 2D Architectural Layout Visualizer (Ground Floor)</h3>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  onAskAI && onAskAI(`Suggest structural beam & column placement for ${width}x${length} ${facing} facing floor plan.`);
                }}
              >
                💬 Ask AI Structural Column Placement
              </button>
            </div>

            <div className="svg-canvas-container" style={{ background: "var(--cream)", border: "1px dashed var(--border)", borderRadius: "12px", padding: "20px", display: "flex", justifyContent: "center" }}>
              <svg viewBox="0 0 800 500" style={{ width: "100%", maxHeight: "480px" }}>
                {/* Plot Boundary */}
                <rect x="50" y="40" width="700" height="420" fill="none" stroke="var(--slate)" strokeWidth="2" strokeDasharray="6 4" />
                <text x="400" y="28" fill="var(--charcoal)" fontSize="13" textAnchor="middle" fontWeight="bold">
                  {facing.toUpperCase()} FACING ROAD ({width}' Width × {length}' Depth Plot Boundary)
                </text>

                {/* Entrance Direction Indicator */}
                <path d="M 400 40 L 400 70 M 393 60 L 400 70 L 407 60" stroke="var(--saffron)" strokeWidth="3" fill="none" />
                <text x="400" y="85" fill="var(--saffron)" fontSize="12" textAnchor="middle" fontWeight="bold">
                  MAIN ENTRANCE ({facing.toUpperCase()})
                </text>

                {/* Setback Dashed Box */}
                <rect x="90" y="95" width="620" height="340" fill="var(--white)" stroke="var(--saffron)" strokeWidth="2" rx="6" />

                {/* Living Room */}
                <rect x="100" y="105" width="340" height="150" fill="rgba(232, 87, 26, 0.08)" stroke="var(--saffron)" strokeWidth="1.5" rx="4" />
                <text x="270" y="170" fill="var(--charcoal)" fontSize="15" fontWeight="bold" textAnchor="middle">Living & Dining Hall</text>
                <text x="270" y="190" fill="var(--slate)" fontSize="11" textAnchor="middle">NE / Center Zone • Vastu Score {planData.vastu_summary.overall_vastu_score}%</text>

                {/* Pooja Room */}
                <rect x="450" y="105" width="120" height="150" fill="rgba(245, 158, 11, 0.12)" stroke="var(--amber)" strokeWidth="1.5" rx="4" />
                <text x="510" y="170" fill="var(--charcoal)" fontSize="13" fontWeight="bold" textAnchor="middle">Pooja Room</text>
                <text x="510" y="190" fill="var(--slate)" fontSize="10" textAnchor="middle">North-East</text>

                {/* Kitchen */}
                <rect x="580" y="105" width="120" height="150" fill="rgba(239, 68, 68, 0.10)" stroke="var(--red)" strokeWidth="1.5" rx="4" />
                <text x="640" y="170" fill="var(--charcoal)" fontSize="13" fontWeight="bold" textAnchor="middle">Kitchen</text>
                <text x="640" y="190" fill="var(--slate)" fontSize="10" textAnchor="middle">South-East</text>

                {/* Master Bedroom */}
                <rect x="100" y="265" width="320" height="160" fill="rgba(37, 99, 235, 0.08)" stroke="var(--blue)" strokeWidth="1.5" rx="4" />
                <text x="260" y="340" fill="var(--charcoal)" fontSize="14" fontWeight="bold" textAnchor="middle">Master Bedroom + Bath</text>
                <text x="260" y="360" fill="var(--slate)" fontSize="11" textAnchor="middle">South-West Zone</text>

                {/* Staircase */}
                <rect x="430" y="265" width="140" height="160" fill="rgba(74, 85, 104, 0.08)" stroke="var(--slate)" strokeWidth="1.5" rx="4" />
                <line x1="430" y1="305" x2="570" y2="305" stroke="var(--border)" strokeWidth="1" strokeDasharray="3 3" />
                <line x1="430" y1="345" x2="570" y2="345" stroke="var(--border)" strokeWidth="1" strokeDasharray="3 3" />
                <line x1="430" y1="385" x2="570" y2="385" stroke="var(--border)" strokeWidth="1" strokeDasharray="3 3" />
                <text x="500" y="340" fill="var(--charcoal)" fontSize="12" fontWeight="bold" textAnchor="middle">RCC Stairs</text>

                {/* Guest Bedroom */}
                <rect x="580" y="265" width="120" height="160" fill="rgba(16, 185, 129, 0.10)" stroke="var(--green)" strokeWidth="1.5" rx="4" />
                <text x="640" y="340" fill="var(--charcoal)" fontSize="13" fontWeight="bold" textAnchor="middle">Bedroom 2</text>
                <text x="640" y="360" fill="var(--slate)" fontSize="10" textAnchor="middle">North-West</text>
              </svg>
            </div>

            {/* Room Details Table */}
            <div className="table-responsive" style={{ marginTop: "24px" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Room Name</th>
                    <th>Direction Zone</th>
                    <th>Dimensions (W × L)</th>
                    <th>Area (sq ft)</th>
                    <th>Vastu Compliance</th>
                    <th>Key Features</th>
                  </tr>
                </thead>
                <tbody>
                  {planData.rooms.map((room) => (
                    <tr key={room.id}>
                      <td>
                        <span style={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "50%", background: room.color, marginRight: "8px" }}></span>
                        <strong style={{ color: "var(--charcoal)" }}>{room.name}</strong>
                      </td>
                      <td style={{ color: "var(--slate)" }}>{room.zone}</td>
                      <td>{room.width}' × {room.length}'</td>
                      <td><strong style={{ color: "var(--charcoal)" }}>{room.area_sqft} sq ft</strong></td>
                      <td><span className="badge-green">{room.vastu_rating}</span></td>
                      <td style={{ fontSize: "13px", color: "var(--slate)" }}>{room.features}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
